import { useTranslation } from "react-i18next";

import { KTSVG } from "../../../../_metronic/helpers";
import { LoadingCard } from "../../../../_metronic/elements/Loading";
import { openAlert } from "../../../../_metronic/elements/NotificationAlert";
import { useErrorHandler } from "../../../../_metronic/helpers/ErrorHandler";
import { useIsMobile } from "../../../shared/responsive";
import { formatFixedNumber } from "../../../shared/numberFormat";
import * as ResellerService from "../services/ResellerService";
import { PLANET_HD_V1_ENABLED } from "../../../config/featureFlags";
import { buildLoginAsUrl } from "../../../routing/impersonationTransport";

const getUserPlanResources = (user) => user?.plan?.resources ?? [];

const getUserUsageEntries = (user) => user?.usage?.usages ?? [];

const normalizeQuantity = (value) => {
  const quantity = Number(value);
  return Number.isFinite(quantity) ? quantity : 0;
};

const getPlanQuantity = (user, resourceId) => {
  const resource = getUserPlanResources(user).find((entry) => entry?.resource_id === resourceId);
  return normalizeQuantity(resource?.quantity);
};

const getUsageQuantity = (user, resourceId) => {
  const usage = getUserUsageEntries(user).find((entry) => entry?.resource === resourceId);
  return normalizeQuantity(usage?.usage);
};

const getRemainingHaValue = (user, resourceId) => getPlanQuantity(user, resourceId) - getUsageQuantity(user, resourceId);

const formatHa = (value) => formatFixedNumber(normalizeQuantity(value));

const getRemainingHa = (user, resourceId) => formatHa(getRemainingHaValue(user, resourceId));

const RESOURCE_LABEL_KEYS = {
  ambients: "reseller:resources.ambients",
  reports_ndvi_ha: "reseller:resources.reports",
  planet_hd_ha: "reseller:resources.planet_hd",
};

const getUserDisplayName = (user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  return fullName || user?.email || '';
};

const getResourceLabel = (t, resourceId) => t(RESOURCE_LABEL_KEYS[resourceId] || "reseller:resources.generic");

const openAssignModal = (setOpen, setUser, resourceId, user) => () => {
  setOpen(resourceId);
  setUser(user);
};

const createLoginHandler = ({ user, errorHandler, t }) => () => {
  if (user.confirmed === false) {
    openAlert("danger", t("reseller:list_users.user_not_confirmed.title"), t("reseller:list_users.user_not_confirmed.message"));
    return;
  }

  const displayName = getUserDisplayName(user);
  const confirmed = window.confirm(t("reseller:list_users.login_as.confirm", {
    name: displayName,
    email: user.email
  }));

  if (!confirmed) {
    return;
  }

  errorHandler(ResellerService.token({
    user_id: user.id
  })).then(res => {
    if (res.ok) {
      window.location.assign(buildLoginAsUrl(res.data.token));
    }
  });
};

const AvailabilityValue = ({ value }) => <>{value} ha</>;

const ResourceAssignButton = ({ className, resourceId, setOpen, setUser, user }) => {
  const { t } = useTranslation();
  const resourceLabel = getResourceLabel(t, resourceId);
  const displayName = getUserDisplayName(user);

  return (
    <button
      type="button"
      aria-label={t("reseller:list_users.table.button.assign_aria", {
        name: displayName,
        resource: resourceLabel
      })}
      className={className}
      onClick={openAssignModal(setOpen, setUser, resourceId, user)}
    >
      {t("reseller:list_users.table.button.assign")}
    </button>
  );
};

const ConfirmedStatus = ({ confirmed }) => {
  if (confirmed) {
    return (
      <KTSVG
        path="/media/icons/duotune/general/gen037.svg"
        className="svg-icon svg-icon-2x svg-icon-success"
      />
    );
  }

  return (
    <KTSVG
      path="/media/icons/duotune/general/gen034.svg"
      className="svg-icon svg-icon-2x svg-icon-danger"
    />
  );
};

const Table = ({ users, setOpen, setUser }) => {
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const safeUsers = Array.isArray(users) ? users : [];

  return (
    <div className="reseller-list-card__table-wrap">
      <table className="table gs-5 gy-5 gx-5 reseller-list-card__table">
        <thead>
          <tr className="fw-bold fs-6 text-gray-800 border-bottom border-gray-200">
            <th>{t("reseller:list_users.table.columns.name")}</th>
            <th>{t("reseller:list_users.table.columns.email")}</th>
            <th>{t("reseller:list_users.table.columns.confirmed")}</th>
            <th>{t("reseller:list_users.table.columns.free_ha_ambients")}</th>
            <th>{t("reseller:list_users.table.columns.free_ha_reports_ndvi")}</th>
            {PLANET_HD_V1_ENABLED && (
              <th>{t("reseller:list_users.table.columns.free_ha_planet_hd")}</th>
            )}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {safeUsers.length < 1 && (
            <tr>
              <td colSpan={PLANET_HD_V1_ENABLED ? 7 : 6} className="reseller-list-card__empty">
                {t("reseller:list_users.not_users")}
              </td>
            </tr>
          )}
          {safeUsers.map(user => {
            const remainingAmbients = getRemainingHa(user, 'ambients');
            const remainingReportsNdvi = getRemainingHa(user, 'reports_ndvi_ha');
            const remainingPlanetHd = getRemainingHa(user, 'planet_hd_ha');

            return (
              <tr key={user.id}>
                <td>{user.first_name} {user.last_name}</td>
                <td>{user.email}</td>
                <td>
                  <ConfirmedStatus confirmed={user.confirmed} />
                </td>
                <td>
                  <div className="reseller-resource-cell">
                    <AvailabilityValue value={remainingAmbients} />
                    <ResourceAssignButton className="btn btn-light-primary btn-sm reseller-list-card__inline-action" resourceId="ambients" setOpen={setOpen} setUser={setUser} user={user} />
                  </div>
                </td>
                <td>
                  <div className="reseller-resource-cell">
                    <AvailabilityValue value={remainingReportsNdvi} />
                    <ResourceAssignButton className="btn btn-light-primary btn-sm reseller-list-card__inline-action" resourceId="reports_ndvi_ha" setOpen={setOpen} setUser={setUser} user={user} />
                  </div>
                </td>
                {PLANET_HD_V1_ENABLED && (
                  <td>
                    <div className="reseller-resource-cell">
                      <AvailabilityValue value={remainingPlanetHd} />
                      <ResourceAssignButton className="btn btn-light-primary btn-sm reseller-list-card__inline-action" resourceId="planet_hd_ha" setOpen={setOpen} setUser={setUser} user={user} />
                    </div>
                  </td>
                )}
                <td>
                  <button type="button" onClick={createLoginHandler({user, errorHandler, t})} className="btn btn-light-primary btn-sm reseller-list-card__login-action">
                    {t("reseller:list_users.table.button.login")}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  );
};

const UserCards = ({ users, setOpen, setUser }) => {
  const { errorHandler } = useErrorHandler();
  const { t } = useTranslation();
  const safeUsers = Array.isArray(users) ? users : [];

  if (safeUsers.length < 1) {
    return <div className="reseller-user-card reseller-user-card--empty">{t("reseller:list_users.not_users")}</div>;
  }

  return (
    <div className="reseller-user-cards">
      {safeUsers.map((user) => {
        const remainingAmbients = getRemainingHa(user, 'ambients');
        const remainingReportsNdvi = getRemainingHa(user, 'reports_ndvi_ha');
        const remainingPlanetHd = getRemainingHa(user, 'planet_hd_ha');

        return (
          <article className="reseller-user-card" key={user.id}>
            <div className="reseller-user-card__header">
              <div>
                <h3 className="reseller-user-card__name">{user.first_name} {user.last_name}</h3>
                <p className="reseller-user-card__email">{user.email}</p>
              </div>
              <div className="reseller-user-card__status" aria-label={t("reseller:list_users.table.columns.confirmed")}>
                <ConfirmedStatus confirmed={user.confirmed} />
              </div>
            </div>

            <div className="reseller-user-card__stats">
              <section className="reseller-user-card__stat">
                <div>
                  <span className="reseller-user-card__stat-label">{t("reseller:list_users.table.columns.free_ha_ambients")}</span>
                  <strong className="reseller-user-card__stat-value"><AvailabilityValue value={remainingAmbients} /></strong>
                </div>
                <button
                  type="button"
                  className="btn btn-light-primary btn-sm reseller-user-card__assign"
                  aria-label={t("reseller:list_users.table.button.assign_aria", {
                    name: getUserDisplayName(user),
                    resource: getResourceLabel(t, "ambients")
                  })}
                  onClick={openAssignModal(setOpen, setUser, "ambients", user)}
                >
                  {t("reseller:list_users.table.button.assign")}
                </button>
              </section>

              <section className="reseller-user-card__stat">
                <div>
                  <span className="reseller-user-card__stat-label">{t("reseller:list_users.table.columns.free_ha_reports_ndvi")}</span>
                  <strong className="reseller-user-card__stat-value"><AvailabilityValue value={remainingReportsNdvi} /></strong>
                </div>
                <button
                  type="button"
                  className="btn btn-light-primary btn-sm reseller-user-card__assign"
                  aria-label={t("reseller:list_users.table.button.assign_aria", {
                    name: getUserDisplayName(user),
                    resource: getResourceLabel(t, "reports_ndvi_ha")
                  })}
                  onClick={openAssignModal(setOpen, setUser, "reports_ndvi_ha", user)}
                >
                  {t("reseller:list_users.table.button.assign")}
                </button>
              </section>

              {PLANET_HD_V1_ENABLED && (
                <section className="reseller-user-card__stat">
                  <div>
                    <span className="reseller-user-card__stat-label">{t("reseller:list_users.table.columns.free_ha_planet_hd")}</span>
                    <strong className="reseller-user-card__stat-value"><AvailabilityValue value={remainingPlanetHd} /></strong>
                  </div>
                  <button
                    type="button"
                    className="btn btn-light-primary btn-sm reseller-user-card__assign"
                    aria-label={t("reseller:list_users.table.button.assign_aria", {
                      name: getUserDisplayName(user),
                      resource: getResourceLabel(t, "planet_hd_ha")
                    })}
                    onClick={openAssignModal(setOpen, setUser, "planet_hd_ha", user)}
                  >
                    {t("reseller:list_users.table.button.assign")}
                  </button>
                </section>
              )}
            </div>

            <button type="button" onClick={createLoginHandler({user, errorHandler, t})} className="btn btn-primary btn-sm reseller-user-card__login">
              {t("reseller:list_users.table.button.login")}
            </button>
          </article>
        );
      })}
    </div>
  );
};

const UserSummary = ({ users }) => {
  const { t } = useTranslation();
  const safeUsers = Array.isArray(users) ? users : [];
  const confirmedUsers = safeUsers.filter((user) => user.confirmed).length;
  const pendingUsers = Math.max(safeUsers.length - confirmedUsers, 0);
  const items = [
    { key: 'total', label: t("reseller:list_users.summary.total"), value: safeUsers.length },
    { key: 'confirmed', label: t("reseller:list_users.summary.confirmed"), value: confirmedUsers },
    { key: 'pending', label: t("reseller:list_users.summary.pending"), value: pendingUsers },
  ];

  return (
    <div className="reseller-summary" aria-label={t("reseller:list_users.summary.aria")}>
      {items.map((item) => (
        <div className="reseller-summary__item" key={item.key}>
          <span className="reseller-summary__label">{item.label}</span>
          <strong className="reseller-summary__value">{item.value}</strong>
        </div>
      ))}
    </div>
  );
};

const ListUsers = ({ setUser, setOpen, loading, users }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const safeUsers = Array.isArray(users) ? users : [];

  if (loading) {
    return <LoadingCard />
  }

  return (
    <>
      <UserSummary users={safeUsers} />
      <div className="card card-custom col-xl-12 col-md-12 reseller-list-card">
        <div className="card-header">
          <h3 className="card-title">{t("reseller:list_users.card_title")}</h3>
        </div>
        <div className="card-body p-0">
          {isMobile ? (
            <div className="reseller-list-card__mobile-body">
              <UserCards users={safeUsers} setUser={setUser} setOpen={setOpen} />
            </div>
          ) : (
            <Table users={safeUsers} setUser={setUser} setOpen={setOpen} />
          )}
        </div>
      </div>
    </>
  );
};

export default ListUsers;
